import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL } from "../api/axios";

const DEFAULT_CONNECTIVITY = Object.freeze({
  state: "online",
  isOnline: true,
  isOfflineLike: false,
  lastCheckedAt: null,
  lastOnlineAt: null,
  checkNow: async () => undefined,
});

const ConnectivityContext = createContext(DEFAULT_CONNECTIVITY);
const ONLINE_INTERVAL_MS = 15000;
const OFFLINE_INTERVAL_MS = 5000;
const CHECK_TIMEOUT_MS = 4000;

export function ConnectivityProvider({ children }) {
  const [state, setState] = useState(() => (navigator.onLine === false ? "offline" : "checking"));
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [lastOnlineAt, setLastOnlineAt] = useState(null);
  const running = useRef(false);

  const checkNow = useCallback(async ({ reconnecting = false } = {}) => {
    if (running.current) return;
    if (navigator.onLine === false) {
      setState("offline");
      setLastCheckedAt(new Date().toISOString());
      return;
    }
    running.current = true;
    if (reconnecting) setState("reconnecting");
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), CHECK_TIMEOUT_MS);
    try {
      const response = await fetch(`${API_BASE_URL}/health/ready`, {
        method: "GET",
        cache: "no-store",
        credentials: "include",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });
      const now = new Date().toISOString();
      setLastCheckedAt(now);
      if (response.ok) {
        setState("online");
        setLastOnlineAt(now);
      } else {
        setState("degraded");
      }
    } catch {
      setLastCheckedAt(new Date().toISOString());
      setState("offline");
    } finally {
      window.clearTimeout(timer);
      running.current = false;
    }
  }, []);

  useEffect(() => {
    checkNow();
    const handleOnline = () => checkNow({ reconnecting: true });
    const handleOffline = () => setState("offline");
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [checkNow]);

  useEffect(() => {
    const interval = state === "online" ? ONLINE_INTERVAL_MS : OFFLINE_INTERVAL_MS;
    const timer = window.setInterval(() => checkNow({ reconnecting: state !== "online" }), interval);
    return () => window.clearInterval(timer);
  }, [checkNow, state]);

  const value = useMemo(() => ({
    state,
    isOnline: state === "online",
    isOfflineLike: state === "offline" || state === "degraded",
    lastCheckedAt,
    lastOnlineAt,
    checkNow,
  }), [checkNow, lastCheckedAt, lastOnlineAt, state]);

  return <ConnectivityContext.Provider value={value}>{children}</ConnectivityContext.Provider>;
}

export function useConnectivity() {
  return useContext(ConnectivityContext);
}
